import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

const EO_NATIVE_HEADERS = {
  client_id: 'bd62fd60-aa08-40fc-8b99-93447fd5940c',
  'Ocp-Apim-Subscription-Key': '5e62198d80a2493fbbfbeca6e064a16b',
};

/**
 * Angular migration of GoldSyncFactory.
 * Uses withCredentials: true. Base URL from environment.scheduler_api_url (or GENERAL_CONFIG).
 */
@Injectable({ providedIn: 'root' })
export class GoldSyncService {
  constructor(private http: HttpClient) {}

  getGoldSyncApiDetails(baseUrl: string): Observable<any> {
    return this.http.get<any>(`${baseUrl}/getGoldSyncApiDetails`, { withCredentials: true });
  }

  getGoldSyncOpenScenarios(baseUrl: string, taxYear: string): Observable<any> {
    return this.http.get<any>(
      `${baseUrl}/getGoldSyncScenarios?taxYear=${encodeURIComponent(taxYear)}`,
      { withCredentials: true }
    );
  }

  syncApiData(
    baseUrl: string,
    taxYear: string,
    jobIds: number[],
    sourceTypes: string[],
    scenarios: string[]
  ): Observable<any> {
    const url =
      `${baseUrl}/syncApiData?taxYearList=${encodeURIComponent(taxYear)}` +
      `&jobIdList=${encodeURIComponent(jobIds.join(','))}` +
      `&sourceTypes=${encodeURIComponent(sourceTypes.join(','))}` +
      `&scenarios=${encodeURIComponent(scenarios.join(','))}`;
    return this.http.post<any>(url, null, { withCredentials: true });
  }

  syncEONativeApiData(
    baseUrl: string,
    selectedJob: { src_tbl_name: string; src_details: string },
    clientDatasource: string
  ): Observable<any> {
    const url =
      `${baseUrl}/syncEOObject?client_ds=${encodeURIComponent(clientDatasource)}` +
      `&object_to_sync=${encodeURIComponent(selectedJob.src_tbl_name)}` +
      `&url=${encodeURIComponent(selectedJob.src_details)}` +
      `&gifcall=y`;
    return this.http.get<any>(url, { headers: new HttpHeaders(EO_NATIVE_HEADERS), withCredentials: true });
  }

  syncEONativeRPData(
    baseUrl: string,
    selectedJob: { src_tbl_name: string },
    clientDatasource: string
  ): Observable<any> {
    const url =
      `${baseUrl}/processEOToRPT?client=${encodeURIComponent(clientDatasource)}` +
      `&object=${encodeURIComponent(selectedJob.src_tbl_name)}`;
    return this.http.get<any>(url, { headers: new HttpHeaders(EO_NATIVE_HEADERS), withCredentials: true });
  }

  updateApiDetails(baseUrl: string, apiDetails: any): Observable<any> {
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post<any>(`${baseUrl}/updateApiDetails`, apiDetails, { headers, withCredentials: true });
  }

  addApiDetails(baseUrl: string, apiDetails: any): Observable<any> {
    const headers = { 'Content-Type': 'application/json' };
    return this.http.post<any>(`${baseUrl}/addApiDetails`, apiDetails, { headers, withCredentials: true });
  }

  getGoldSyncObjectDataCount(baseUrl: string, objectName: string): Observable<any> {
    return this.http.get<any>(
      `${baseUrl}/getGoldSyncObjectDataCount?objectName=${encodeURIComponent(objectName)}`,
      { withCredentials: true }
    );
  }

  /**
   * Mirrors legacy GoldSyncFactory.getLogHistory / requestUrlBuilder:
   * only defined query params are sent (taxYearList/jobIdList omitted when empty).
   */
  getLogHistory(
    baseUrl: string,
    params: {
      taxYearList?: (string | number)[];
      jobIdList?: (string | number)[];
      page?: number;
      size?: number;
      sortBy?: string;
      sortByDirection?: string;
    }
  ): Observable<any> {
    const parts: string[] = [];
    const append = (name: string, value: string | number | undefined): void => {
      if (value === undefined || value === null || value === '') return;
      parts.push(`${encodeURIComponent(name)}=${encodeURIComponent(String(value))}`);
    };

    if (params.taxYearList?.length) {
      append('taxYearList', params.taxYearList.map(String).join(','));
    }
    if (params.jobIdList?.length) {
      append('jobIdList', params.jobIdList.map(String).join(','));
    }
    append('page', params.page);
    append('size', params.size);
    append('sortBy', params.sortBy);
    append('sortByDirection', params.sortByDirection);

    const qs = parts.length ? `?${parts.join('&')}` : '';
    return this.http.get<any>(`${baseUrl}/getGoldSyncLogHistory${qs}`, { withCredentials: true });
  }

  getDataSources(baseUrl: string): Observable<any> {
    return this.http.get<any>(`${baseUrl}/getDataSources`, { withCredentials: true });
  }
}
